import type {
  BlogCommentApi,
  BlogCommentCreateResponse,
  BlogCommentReplyApi,
  BlogCommentsListResponse,
} from './blog-comments.api-model';

/** Un comentario listo para pintar. Las respuestas llevan `replies: []`. */
export interface BlogCommentView {
  id: number;
  authorName: string;
  body: string;
  /** ISO 8601 tal cual llega; se formatea con `formatBlogDate` al pintar. */
  createdAt: string;
  isStaff: boolean;
  edited: boolean;
  replies: BlogCommentView[];
}

/** Una página del hilo. `total` y `totalPages` cuentan solo raíces (B88). */
export interface BlogCommentsPage {
  comments: BlogCommentView[];
  total: number;
  page: number;
  totalPages: number;
}

/** Resultado del `POST`: `message` es dato de la API, `null` si no llega. */
export interface BlogCommentCreated {
  id: number;
  message: string | null;
}

/**
 * Booleano defensivo: la API de la casa ha llegado a emitir booleanos como
 * `0/1` o como cadena según el driver. Solo `true`, `1`, `'1'` y `'true'` son
 * verdad; todo lo demás (`'0'`, `'false'`, ausente…) es falso. `!!` no sirve:
 * `!!'0'` es `true`.
 */
function toBool(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

// `Number()` defensivo por el mismo motivo: enteros que llegan como cadena.
function toReplyView(api: BlogCommentReplyApi): BlogCommentView {
  return {
    id: Number(api.id),
    authorName: api.author_name,
    body: api.body,
    createdAt: api.created_at,
    isStaff: toBool(api.is_staff),
    edited: toBool(api.edited),
    replies: [],
  };
}

export function toBlogCommentView(api: BlogCommentApi): BlogCommentView {
  return {
    ...toReplyView(api),
    replies: (api.replies ?? []).map(toReplyView),
  };
}

export function toBlogCommentsPage(res: BlogCommentsListResponse): BlogCommentsPage {
  return {
    comments: (res.data ?? []).map(toBlogCommentView),
    total: Number(res.meta.total),
    page: Number(res.meta.page),
    totalPages: Number(res.meta.total_pages),
  };
}

/**
 * `message` es dato de la API y viaja verbatim (B113): `trim()` solo decide si
 * está en blanco; si tiene contenido, se devuelve el string original.
 */
export function toBlogCommentCreated(res: BlogCommentCreateResponse): BlogCommentCreated {
  const message = typeof res.message === 'string' ? res.message : '';
  return { id: Number(res.data.id), message: message.trim() === '' ? null : message };
}
